import { tenantApi } from '@shared/api/tenantApi';
import { unwrap, unwrapList } from '@shared/api/unwrap';

export type NotificationType =
  | 'transfer_request'
  | 'transfer_approved'
  | 'transfer_rejected'
  | 'member_status_change'
  | 'census_deadline'
  | 'system_alert'
  | 'event_registration'
  | 'support_comment'
  | 'classified_pending'
  | 'classified_approved'
  | 'classified_declined'
  | 'general';

export interface Notification {
  notification_id: string;
  title: string;
  message: string;
  notification_type: NotificationType;
  is_read: boolean;
  created_at: string;
  read_at?: string;
  priority?: 'low' | 'medium' | 'high';
  action_url?: string;
  related_id?: string;
  // Deep-link metadata (mirrors the push data payload) — drives routing + tenant switch.
  tenant_id?: string;
  module?: string;
  related_entity_type?: string;
  metadata?: Record<string, unknown> | null;
}

export interface NotificationFilters {
  is_read?: boolean;
  limit?: number;
  offset?: number;
}

export async function fetchNotifications(
  filters: NotificationFilters = {},
): Promise<Notification[]> {
  const { data } = await tenantApi.get('/notifications', { params: filters });
  return unwrapList<Notification>(data);
}

export async function fetchUnreadNotificationCount(): Promise<number> {
  const { data } = await tenantApi.get('/notifications/unread-count');
  const payload = unwrap<{ unread_count: number }>(data);
  return Number(payload.unread_count) || 0;
}

export async function markNotificationRead(notificationId: string): Promise<void> {
  await tenantApi.put(`/notifications/${notificationId}/read`, {});
}

export async function markAllNotificationsRead(): Promise<void> {
  await tenantApi.put('/notifications/mark-all-read', {});
}

export interface RegisterPushTokenInput {
  token: string;
  platform: string;
  app_id?: string;
  device_id?: string | null;
}

export async function registerPushToken(input: RegisterPushTokenInput): Promise<void> {
  await tenantApi.post('/notifications/push-token', input);
}

export async function unregisterPushToken(token: string): Promise<void> {
  await tenantApi.delete('/notifications/push-token', { data: { token } });
}
